import { API_URL, CLERK_FAPI_URL } from "./urls.js";
import { withNextStep } from "./guidance.js";
import { CARRIER_VERSION } from "../../version.js";

/**
 * Headless account creation.
 *
 * `carrier signup` used to open a browser, which made the terminal able to do
 * everything with a Carrier account except get one. This runs the whole thing
 * in the shell: collect the details, create the account, verify the emailed
 * code, land in an organization.
 *
 * The Clerk calls go through `apps/api`, not straight to Clerk. That is where
 * the sign-up rate limit lives, and a limit the CLI enforces on itself is not a
 * limit — the attacker has the CLI too. The one thing read directly from Clerk
 * is `/v1/environment`, because it is public, unauthenticated configuration and
 * the answer decides what this command must ask the human before it starts.
 *
 * Consent is the reason that check is not optional. Sending `legal_accepted`
 * unconditionally would record a consent nobody was shown; omitting it when the
 * instance requires it fails the sign-up with a Clerk error nobody can act on.
 * So: read the setting, and when consent is required, show the actual terms and
 * privacy URLs and make acceptance an explicit answer.
 *
 * This is NOT `lib/clerk.ts`. That file provisions Clerk *applications* for
 * white-label storefronts through the Platform API — a different API with a
 * different lifecycle that happens to share a vendor name.
 */

/** Prompt surface, injected so the flow is testable without a terminal. */
export interface SignupIO {
  /** Free text. Returns null when the user cancels. */
  text(message: string, opts?: { placeholder?: string }): Promise<string | null>;
  /** Masked entry. Returns null when the user cancels. */
  password(message: string): Promise<string | null>;
  /** Yes/no. Returns null when the user cancels. */
  confirm(message: string): Promise<boolean | null>;
  /** Progress line; optional so tests can ignore it. */
  step?(message: string): void;
  /** Block of context, e.g. the consent links. */
  note?(body: string, title?: string): void;
}

export interface SignupResult {
  ok: boolean;
  /** Safe to print — never contains a password or client token. */
  message: string;
  email?: string;
  organizationId?: string;
  organizationName?: string;
  /** True when the user backed out; the caller should not treat it as failure. */
  cancelled?: boolean;
}

export interface SignupOptions {
  io: SignupIO;
  fetchImpl?: typeof fetch;
  apiUrl?: string;
  clerkUrl?: string;
  /** Attempts allowed at the emailed code before giving up. */
  maxCodeAttempts?: number;
}

/** What `/v1/environment` tells us that changes this command's behaviour. */
export interface SignupEnvironment {
  legalConsentRequired: boolean;
  captchaEnabled: boolean;
  termsUrl?: string;
  privacyUrl?: string;
}

const DEFAULT_MAX_CODE_ATTEMPTS = 3;

interface ClerkEnvironmentResponse {
  user_settings?: {
    sign_up?: { legal_consent_enabled?: boolean; captcha_enabled?: boolean };
  };
  display_config?: { terms_url?: string | null; privacy_policy_url?: string | null };
}

export function parseEnvironment(raw: unknown): SignupEnvironment {
  const env = (raw ?? {}) as ClerkEnvironmentResponse;
  const signUp = env.user_settings?.sign_up ?? {};
  const display = env.display_config ?? {};
  const result: SignupEnvironment = {
    legalConsentRequired: signUp.legal_consent_enabled === true,
    captchaEnabled: signUp.captcha_enabled === true,
  };
  if (display.terms_url) result.termsUrl = display.terms_url;
  if (display.privacy_policy_url) result.privacyUrl = display.privacy_policy_url;
  return result;
}

export async function readSignupEnvironment(
  fetchImpl: typeof fetch,
  clerkUrl: string,
): Promise<SignupEnvironment> {
  const url =
    `${clerkUrl}/v1/environment` +
    `?__clerk_api_version=2025-04-10&_clerk_js_version=5.0.0`;
  const res = await fetchImpl(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`Clerk environment returned ${res.status}`);
  return parseEnvironment(await res.json());
}

/** Good enough to catch a typo before a round trip; Clerk does the real check. */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

interface ApiErrorBody {
  error?: string;
  code?: string;
  retry_after_seconds?: number;
}

/**
 * Read an error out of an `apps/api` response.
 *
 * Only the `error` string is ever surfaced. The request body that produced it
 * holds a password and a client token, so nothing else from this exchange is
 * safe to print.
 */
async function apiError(res: Response): Promise<{ message: string; code?: string }> {
  let body: ApiErrorBody = {};
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    // Non-JSON error page: the status is all we can honestly report.
  }
  const out: { message: string; code?: string } = {
    message: body.error ?? `Carrier API returned ${res.status}`,
  };
  if (body.code) out.code = body.code;
  if (res.status === 429 && body.retry_after_seconds) {
    const hours = Math.ceil(body.retry_after_seconds / 3600);
    out.message += ` Try again in about ${hours}h, or sign up in a browser: carrier open signup`;
  }
  return out;
}

function cancelled(): SignupResult {
  return { ok: false, cancelled: true, message: "Sign-up cancelled." };
}

export async function signup(opts: SignupOptions): Promise<SignupResult> {
  const io = opts.io;
  const fetchImpl = opts.fetchImpl ?? fetch;
  const apiUrl = opts.apiUrl ?? API_URL;
  const clerkUrl = opts.clerkUrl ?? CLERK_FAPI_URL;
  const maxAttempts = opts.maxCodeAttempts ?? DEFAULT_MAX_CODE_ATTEMPTS;

  let environment: SignupEnvironment;
  try {
    environment = await readSignupEnvironment(fetchImpl, clerkUrl);
  } catch (e) {
    return {
      ok: false,
      message: withNextStep(
        `Could not read sign-up settings: ${e instanceof Error ? e.message : String(e)}`,
        [
          "Verify outbound HTTPS to clerk.carrier.llc",
          "Sign up in a browser instead: carrier open signup",
        ],
      ),
    };
  }

  // A CAPTCHA is a browser challenge by construction. Failing here beats
  // collecting a password and a name first and failing on the create call.
  if (environment.captchaEnabled) {
    return {
      ok: false,
      message: withNextStep("Sign-up on this instance requires a CAPTCHA, which needs a browser.", [
        "Sign up in a browser: carrier open signup",
        "Then come back and run: carrier login",
      ]),
    };
  }

  const email = await io.text("Email address");
  if (email === null) return cancelled();
  if (!looksLikeEmail(email.trim())) {
    return { ok: false, message: `"${email.trim()}" does not look like an email address.` };
  }

  const firstName = await io.text("First name");
  if (firstName === null) return cancelled();
  const lastName = await io.text("Last name");
  if (lastName === null) return cancelled();
  const password = await io.password("Password");
  if (password === null) return cancelled();

  let legalAccepted = false;
  if (environment.legalConsentRequired) {
    io.note?.(
      [
        `Terms: ${environment.termsUrl ?? "https://carrier.llc/terms"}`,
        `Privacy: ${environment.privacyUrl ?? "https://carrier.llc/privacy"}`,
      ].join("\n"),
      "Before you continue",
    );
    const accepted = await io.confirm("Do you accept the terms and privacy policy?");
    if (accepted === null) return cancelled();
    if (!accepted) {
      return { ok: false, message: "An account cannot be created without accepting the terms." };
    }
    legalAccepted = true;
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "User-Agent": `carrier-cli/${CARRIER_VERSION}`,
  };

  io.step?.("Creating your account");
  let started: { sign_up_id: string; client_token: string; verification_required: boolean };
  try {
    const res = await fetchImpl(`${apiUrl}/v1/signup`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        email_address: email.trim(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        ...(legalAccepted ? { legal_accepted: true } : {}),
      }),
    });
    if (!res.ok) {
      const err = await apiError(res);
      return {
        ok: false,
        message: withNextStep(err.message, [
          "Already have an account? carrier login",
          "Or sign up in a browser: carrier open signup",
        ]),
      };
    }
    const body = (await res.json()) as {
      data?: { sign_up_id?: string; client_token?: string; verification_required?: boolean };
    };
    if (!body.data?.sign_up_id || !body.data.client_token) {
      return { ok: false, message: "Sign-up started but the response was incomplete." };
    }
    started = {
      sign_up_id: body.data.sign_up_id,
      client_token: body.data.client_token,
      verification_required: body.data.verification_required !== false,
    };
  } catch (e) {
    return {
      ok: false,
      message: withNextStep(
        `Could not reach the Carrier API: ${e instanceof Error ? e.message : String(e)}`,
        ["Verify outbound HTTPS to api.carrier.llc"],
      ),
    };
  }

  if (started.verification_required) {
    io.note?.(`We sent a code to ${email.trim()}.`, "Check your email");
  }

  let lastError = "Verification failed.";
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const code = await io.text("Verification code", { placeholder: "123456" });
    if (code === null) return cancelled();

    io.step?.("Verifying");
    let res: Response;
    try {
      res = await fetchImpl(`${apiUrl}/v1/signup/verify`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          sign_up_id: started.sign_up_id,
          client_token: started.client_token,
          code: code.trim(),
        }),
      });
    } catch (e) {
      return {
        ok: false,
        message: withNextStep(
          `Could not reach the Carrier API: ${e instanceof Error ? e.message : String(e)}`,
          ["Your account may exist — try: carrier login"],
        ),
      };
    }

    if (res.ok) {
      const body = (await res.json()) as {
        data?: { organization_id?: string; organization_name?: string; email_address?: string };
      };
      const result: SignupResult = {
        ok: true,
        message: "Account created.",
        email: body.data?.email_address ?? email.trim(),
      };
      if (body.data?.organization_id) result.organizationId = body.data.organization_id;
      if (body.data?.organization_name) result.organizationName = body.data.organization_name;
      return result;
    }

    const err = await apiError(res);
    lastError = err.message;
    // Only a wrong code is worth another go. Anything else — an expired code, a
    // rate limit, a Clerk outage — will fail the same way on every retry, and
    // asking again would just walk the user into the attempt ceiling.
    if (err.code !== "form_code_incorrect") {
      return {
        ok: false,
        message: withNextStep(err.message, [
          "Your account may already exist — try: carrier login",
          "Or finish in a browser: carrier open signup",
        ]),
      };
    }
    if (attempt < maxAttempts) io.note?.("That code was not right. Try again.", "Incorrect code");
  }

  return {
    ok: false,
    message: withNextStep(lastError, [
      `No attempts left after ${maxAttempts} tries.`,
      "Start over: carrier signup",
      "Or finish in a browser: carrier open signup",
    ]),
  };
}
